import React, { useEffect, useRef, useState } from 'react';

import {
  Circle,
  Mic,
  MicOff,
  PlayCircle,
  Settings,
  Square,
  Volume2,
} from 'lucide-react';
import { toast } from 'sonner';

import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';

import type { SpeechAnalysis } from '~/routes/api.analyze';
import { uploadLargeFile } from '~/utils/upload';
import type { UploadProgress } from '~/utils/upload';

import AIAnalysis from './AIAnalysis';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';

// Utility function to log only on client side
const logOnClient = (
  level: 'info' | 'warn' | 'error',
  message: string,
  data?: any
) => {
  if (typeof window !== 'undefined') {
    console[level](`[VideoRecorder] ${message}`, data);
  }
};

interface AudioDevice {
  deviceId: string;
  label: string;
}

interface VideoRecorderProps {
  onRecordingComplete: (blob: Blob) => void;
  onCleanup?: () => void;
}

type TestRecordingState = 'inactive' | 'recording' | 'playing';
type RecordingState = 'idle' | 'recording' | 'complete';

const VideoRecorder: React.FC<VideoRecorderProps> = ({
  onRecordingComplete,
  onCleanup,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const startTimeRef = useRef<number>(0);
  const [recordingState, setRecordingState] = useState<RecordingState>('idle');
  const [error, setError] = useState<string | null>(null);
  const [analysisData, setAnalysisData] = useState<SpeechAnalysis | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  const [isRecording, setIsRecording] = useState(false);
  const [timer, setTimer] = useState(0);
  const [cameraReady, setCameraReady] = useState(false);
  const [permissionDenied, setPermissionDenied] = useState(false);

  const [audioDevices, setAudioDevices] = useState<AudioDevice[]>([]);
  const [selectedAudioDevice, setSelectedAudioDevice] = useState<string>('');
  const [isTestingAudio, setIsTestingAudio] = useState(false);
  const testAudioRef = useRef<HTMLAudioElement | null>(null);
  const [testRecordingState, setTestRecordingState] =
    useState<TestRecordingState>('inactive');
  const testMediaRecorderRef = useRef<MediaRecorder | null>(null);
  const testAudioChunksRef = useRef<Blob[]>([]);
  const testAudioPlayerRef = useRef<HTMLAudioElement | null>(null);
  const timerRef = useRef<NodeJS.Timeout>();
  const [uploadProgress, setUploadProgress] = useState<UploadProgress | null>(
    null
  );

  // Format timer as MM:SS
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs
      .toString()
      .padStart(2, '0')}`;
  };

  // Initialize camera
  useEffect(() => {
    const setupCamera = async (audioDeviceId?: string) => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            width: { ideal: 1280 },
            height: { ideal: 720 },
            frameRate: { ideal: 30 },
          },
          audio: {
            deviceId: audioDeviceId ? { exact: audioDeviceId } : undefined,
            echoCancellation: true,
            noiseSuppression: true,
          },
        });

        // Stop existing tracks if any
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => track.stop());
        }

        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          setCameraReady(true);
        }
      } catch (error) {
        logOnClient('error', 'Failed to access camera and microphone', {
          metadata: { error },
        });
        setPermissionDenied(true);
        toast.error('Camera or microphone access denied');
      }
    };

    setupCamera(selectedAudioDevice);

    // Cleanup
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, [selectedAudioDevice]);

  // Handle timer
  useEffect(() => {
    let interval: number | undefined;

    if (isRecording && timer > 0) {
      interval = window.setInterval(() => {
        setTimer(prevTimer => prevTimer - 1);
      }, 1000);
    } else if (isRecording && timer === 0) {
      stopRecording();
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isRecording, timer]);

  const stopRecording = () => {
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state !== 'inactive'
    ) {
      mediaRecorderRef.current.stop();
      setRecordingState('complete');

      // Stop all tracks
      mediaRecorderRef.current.stream
        .getTracks()
        .forEach(track => track.stop());
    }
  };

  // Set up onstop handler before starting
  useEffect(() => {
    if (!mediaRecorderRef.current) return;

    mediaRecorderRef.current.ondataavailable = event => {
      if (event.data.size > 0) {
        chunksRef.current.push(event.data);
      }
    };

    mediaRecorderRef.current.onstop = async () => {
      const blob = new Blob(chunksRef.current, { type: 'video/webm' });

      const endTime = Date.now();
      const durationInSeconds = Math.round(
        (endTime - startTimeRef.current) / 1000
      );

      if (durationInSeconds < 3) {
        logOnClient('warn', 'Recording too short', {
          metadata: { duration: durationInSeconds },
        });
        toast.error('Recording must be at least 3 seconds long');
        resetRecording();
        return;
      }

      try {
        const videoFile = new File([blob], 'recording.webm', {
          type: 'video/webm;codecs=vp9,opus',
        });

        const uploadResult = await uploadLargeFile(videoFile, progress => {
          setUploadProgress(progress);
        });

        if (uploadResult.success) {
          toast.success('Recording uploaded successfully!');

          // Start transcription
          const transcribeResponse = await fetch('/api/transcribe', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              fileId: uploadResult.file.id,
              duration: durationInSeconds,
            }),
          });

          if (!transcribeResponse.ok) {
            throw new Error('Failed to start transcription');
          }

          const transcribeData = await transcribeResponse.json();
          toast.success('Transcription started');
          onRecordingComplete(blob);
        }
      } catch (error) {
        logOnClient('error', 'Processing error', {
          metadata: { error },
        });
        setUploadProgress(null);
        toast.error(
          error instanceof Error ? error.message : 'Failed to process recording'
        );
        onRecordingComplete(blob);
      }
    };
  }, [mediaRecorderRef.current]);

  const startRecording = async () => {
    try {
      if (!streamRef.current) {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            width: { ideal: 1280 }, // 720p
            height: { ideal: 720 },
            frameRate: { ideal: 30 },
          },
          audio: true,
        });
        streamRef.current = stream;
      }

      chunksRef.current = [];
      startTimeRef.current = Date.now();

      // Try to use iOS-compatible formats first for better mobile support
      const codecOptions = [
        // Option 1: H.264 + AAC in MP4 (best for iOS/Safari)
        { mimeType: 'video/mp4;codecs=h264,aac', container: 'mp4' },
        // Option 2: H.264 + AAC in WebM (iOS 17.4+)
        { mimeType: 'video/webm;codecs=h264,aac', container: 'webm' },
        // Option 3: VP9 + Opus (Chrome/Firefox, not iOS)
        { mimeType: 'video/webm;codecs=vp9,opus', container: 'webm' },
        // Option 4: VP8 + Opus (older Chrome/Firefox)
        { mimeType: 'video/webm;codecs=vp8,opus', container: 'webm' },
        // Option 5: No codec specified (browser default)
        { mimeType: 'video/webm', container: 'webm' },
      ];

      let selectedOptions = codecOptions.find(opt => MediaRecorder.isTypeSupported(opt.mimeType));
      
      if (!selectedOptions) {
        logOnClient('warn', 'No video codec supported, using browser default');
        selectedOptions = { mimeType: '', container: 'webm' };
      }

      logOnClient('info', 'Selected video codec', {
        metadata: { 
          mimeType: selectedOptions.mimeType || 'default',
          container: selectedOptions.container,
          isIOS: /iPhone|iPad|iPod/.test(navigator.userAgent)
        }
      });

      const options: MediaRecorderOptions = {
        ...(selectedOptions.mimeType && { mimeType: selectedOptions.mimeType }),
        videoBitsPerSecond: 2500000, // 2.5 Mbps for good quality at 720p
        audioBitsPerSecond: 128000, // 128 kbps for clear audio
      };

      mediaRecorderRef.current = new MediaRecorder(streamRef.current, options);
      mediaRecorderRef.current.start();
      setRecordingState('recording');
      setIsRecording(true);
      toast.dismiss('recording-started');
      toast.success('Practice started', { id: 'recording-started' });
    } catch (error) {
      logOnClient('error', 'Failed to start recording', {
        metadata: { error },
      });
      toast.error('Failed to start recording');
    }
  };

  // Add new function to get audio devices
  const getAudioDevices = async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const audioInputs = devices
        .filter(device => device.kind === 'audioinput')
        .map(device => ({
          deviceId: device.deviceId,
          label: device.label || `Microphone ${device.deviceId.slice(0, 5)}...`,
        }));
      setAudioDevices(audioInputs);

      // Set default device if none selected
      if (!selectedAudioDevice && audioInputs.length > 0) {
        setSelectedAudioDevice(audioInputs[0].deviceId);
      }
    } catch (error) {
      logOnClient('error', 'Failed to get audio devices', {
        metadata: { error },
      });
      toast.error('Could not get audio devices');
    }
  };

  // Add audio testing functionality
  const startAudioTest = async () => {
    if (!streamRef.current) return;

    setIsTestingAudio(true);

    // Create a new audio context
    const audioContext = new AudioContext();
    const source = audioContext.createMediaStreamSource(streamRef.current);
    const destination = audioContext.createMediaStreamDestination();
    source.connect(destination);

    // Create an audio element for monitoring
    if (!testAudioRef.current) {
      testAudioRef.current = new Audio();
      testAudioRef.current.srcObject = destination.stream;
    }

    try {
      await testAudioRef.current.play();
      toast.success('Testing audio... Speak to test your microphone');
    } catch (error) {
      logOnClient('error', 'Failed to test audio', {
        metadata: { error },
      });
      toast.error('Could not test audio');
    }
  };

  const stopAudioTest = () => {
    if (testAudioRef.current) {
      testAudioRef.current.pause();
      testAudioRef.current.srcObject = null;
      testAudioRef.current = null;
    }
    setIsTestingAudio(false);
  };

  // Add useEffect for device enumeration
  useEffect(() => {
    getAudioDevices();
    navigator.mediaDevices.addEventListener('devicechange', getAudioDevices);

    return () => {
      navigator.mediaDevices.removeEventListener(
        'devicechange',
        getAudioDevices
      );
    };
  }, []);

  // Add new function for test recording
  const startTestRecording = async () => {
    try {
      if (!streamRef.current) {
        toast.error('No audio stream available');
        return;
      }

      testMediaRecorderRef.current = new MediaRecorder(streamRef.current);
      testAudioChunksRef.current = [];

      testMediaRecorderRef.current.ondataavailable = event => {
        if (event.data.size > 0) {
          testAudioChunksRef.current.push(event.data);
        }
      };

      testMediaRecorderRef.current.start();
      setTestRecordingState('recording');
      toast.success('Practice started', { id: 'recording-started' });
    } catch (error) {
      logOnClient('error', 'Failed to start test recording', {
        metadata: { error },
      });
      toast.error('Failed to start test recording');
    }
  };

  const stopAndPlayTestRecording = async () => {
    if (!testMediaRecorderRef.current || testRecordingState !== 'recording')
      return;

    try {
      const mediaRecorder = testMediaRecorderRef.current;

      // Create a promise to handle the stop event
      const stopPromise = new Promise(resolve => {
        mediaRecorder.onstop = () => resolve(true);
      });

      mediaRecorder.stop();
      await stopPromise;

      if (testAudioChunksRef.current.length === 0) {
        toast.error('No audio recorded');
        setTestRecordingState('inactive');
        return;
      }

      const audioBlob = new Blob(testAudioChunksRef.current, {
        type: 'audio/webm',
      });
      const audioUrl = URL.createObjectURL(audioBlob);

      if (!testAudioPlayerRef.current) {
        testAudioPlayerRef.current = new Audio();
      }

      testAudioPlayerRef.current.src = audioUrl;
      testAudioPlayerRef.current.onended = () => {
        setTestRecordingState('inactive');
        URL.revokeObjectURL(audioUrl);
      };

      testAudioPlayerRef.current.onplay = () => {
        setTestRecordingState('playing');
      };

      await testAudioPlayerRef.current.play();
    } catch (error) {
      logOnClient('error', 'Failed to stop test recording', {
        metadata: { error },
      });
      toast.error('Failed to play test recording');
      setTestRecordingState('inactive');
    }
  };

  // Clean up test recording resources
  const cleanupTestRecording = () => {
    if (testAudioPlayerRef.current) {
      testAudioPlayerRef.current.pause();
      testAudioPlayerRef.current.src = '';
      testAudioPlayerRef.current = null;
    }
    testAudioChunksRef.current = [];
    setTestRecordingState('inactive');
  };

  // Replace the AudioSettings component with direct settings content
  const AudioSettingsContent = () => (
    <div className="flex items-center gap-4">
      <div className="w-[200px]">
        <Select
          value={selectedAudioDevice || 'default'}
          onValueChange={value => {
            setSelectedAudioDevice(value);
            cleanupTestRecording();
          }}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select microphone" />
          </SelectTrigger>
          <SelectContent>
            {audioDevices.length > 0 ? (
              audioDevices.map(device => (
                <SelectItem
                  key={device.deviceId}
                  value={device.deviceId || 'default'}
                >
                  {device.label}
                </SelectItem>
              ))
            ) : (
              <SelectItem value="default">Default Microphone</SelectItem>
            )}
          </SelectContent>
        </Select>
      </div>

      <Button
        onClick={() => {
          if (testRecordingState === 'inactive') {
            startTestRecording();
          } else if (testRecordingState === 'recording') {
            stopAndPlayTestRecording();
          }
        }}
        type="button"
        variant="default"
        className="flex-1"
      >
        <Mic className="h-4 w-4 mr-2" />
        {testRecordingState === 'inactive'
          ? 'Start Test Recording'
          : 'Stop Recording'}
      </Button>
    </div>
  );

  const pollTranscriptionStatus = async (analysisId: string) => {
    let attempts = 0;
    const maxAttempts = 60; // 5 minutes with 5-second intervals

    while (attempts < maxAttempts) {
      try {
        const response = await fetch(`/api/transcribe/${analysisId}/status`);
        const data = await response.json();

        if (data.status === 'completed') {
          return data;
        }

        if (data.status === 'failed') {
          throw new Error(data.error || 'Transcription failed');
        }

        await new Promise(resolve => setTimeout(resolve, 5000));
        attempts++;
      } catch (error) {
        throw error;
      }
    }

    throw new Error('Transcription took too long');
  };

  const resetRecording = () => {
    chunksRef.current = [];
    setRecordingState('idle');
    setIsRecording(false);
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current = null;
    }
  };

  if (permissionDenied) {
    return (
      <div className="h-full flex flex-col items-center justify-center p-8 bg-muted/30">
        <MicOff className="h-20 w-20 text-muted-foreground mb-6" />
        <h2 className="text-2xl font-semibold mb-3">Camera Access Required</h2>
        <p className="text-center text-muted-foreground max-w-md mb-6">
          To use the speaking practice feature, please allow access to your
          camera and microphone.
        </p>
        <Button onClick={() => window.location.reload()}>Try Again</Button>
      </div>
    );
  }

  if (recordingState === 'complete') {
    return (
      <AIAnalysis
        videoBlob={
          chunksRef.current.length > 0
            ? new Blob(chunksRef.current, {
                type: 'video/webm;codecs=vp9,opus',
              })
            : null
        }
        analysisData={analysisData}
        isAnalyzing={isAnalyzing}
      />
    );
  }

  return (
    <div className="relative h-full flex flex-col items-center bg-muted/30">
      <div className="w-full max-w-4xl mx-auto flex-1 flex flex-col items-center justify-center p-4">
        {/* Video preview */}
        <div className="relative w-full max-w-3xl aspect-video bg-black rounded-xl overflow-hidden shadow-xl mb-8">
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted={true}
            className="w-full h-full object-cover"
          >
            <track kind="captions" />
          </video>

          {/* Timer overlay */}
          {isRecording && (
            <div className="absolute top-4 right-4 bg-background/80 backdrop-blur-sm px-4 py-2 rounded-full flex items-center">
              <div className="h-3 w-3 rounded-full bg-red-600 mr-2 animate-pulse"></div>
              <span className="font-mono font-medium">{formatTime(timer)}</span>
            </div>
          )}

          {/* Loading overlay */}
          {!cameraReady && (
            <div className="absolute inset-0 flex items-center justify-center bg-background/50 backdrop-blur-sm">
              <div className="h-12 w-12 rounded-full border-4 border-primary/30 border-t-primary animate-spin"></div>
            </div>
          )}

          {/* Upload Progress Overlay */}
          {uploadProgress && (
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center">
              <div className="bg-background p-6 rounded-lg shadow-xl max-w-md w-full">
                <div className="space-y-4">
                  <div className="flex justify-between text-sm">
                    <span>Uploading video...</span>
                    <span>{uploadProgress.percentage.toFixed(1)}%</span>
                  </div>

                  <div className="h-2 bg-muted rounded-full overflow-hidden">
                    <div
                      className="h-full bg-primary transition-all duration-300"
                      style={{ width: `${uploadProgress.percentage}%` }}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4 text-sm text-muted-foreground">
                    <div>
                      <p>Upload Speed</p>
                      <p className="font-medium text-foreground">
                        {(uploadProgress.speed / (1024 * 1024)).toFixed(2)} MB/s
                      </p>
                    </div>
                    <div>
                      <p>Time Remaining</p>
                      <p className="font-medium text-foreground">
                        {Math.ceil(uploadProgress.estimatedTimeRemaining)}s
                      </p>
                    </div>
                    <div>
                      <p>Chunks</p>
                      <p className="font-medium text-foreground">
                        {uploadProgress.currentChunk} /{' '}
                        {uploadProgress.totalChunks}
                      </p>
                    </div>
                    <div>
                      <p>Size</p>
                      <p className="font-medium text-foreground">
                        {(uploadProgress.uploadedBytes / (1024 * 1024)).toFixed(
                          1
                        )}{' '}
                        /
                        {(uploadProgress.totalBytes / (1024 * 1024)).toFixed(1)}{' '}
                        MB
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Audio Settings */}
        <div className="w-full max-w-md mb-6">
          <AudioSettingsContent />
        </div>

        {/* Recording Controls */}
        <div className="flex items-center gap-4">
          {!isRecording ? (
            <Button
              onClick={startRecording}
              disabled={!cameraReady}
              className="rounded-full px-6 py-6 h-auto bg-primary hover:bg-primary/90 text-white transition-all shadow"
            >
              <PlayCircle className="mr-2 h-5 w-5" />
              Start Recording
            </Button>
          ) : (
            <Button
              onClick={stopRecording}
              className="rounded-full px-6 py-6 h-auto transition-all shadow bg-destructive hover:bg-destructive/90"
            >
              <div className="mr-2 h-3 w-3 rounded-full bg-white recording-pulse"></div>
              Stop Recording
            </Button>
          )}
        </div>
      </div>

      {/* Instructions */}
      <div className="p-6 w-full bg-background border-t">
        <div className="max-w-2xl mx-auto">
          <h3 className="text-lg font-medium mb-2">Tips for best results:</h3>
          <ul className="text-sm text-muted-foreground space-y-1">
            <li>• Find a quiet environment with good lighting</li>
            <li>• Position yourself in the center of the frame</li>
            <li>• Speak clearly and at a moderate pace</li>
            <li>• Practice will be recorded for 2 minutes</li>
            <li>• You can stop the recording at any time</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default VideoRecorder;
